import { Link } from '@tanstack/react-router'
import { type Image } from '@/api/accommodation/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ImagePlaceholder } from '@/components/ui/ImagePlaceholder'
import Discount from '@/components/money/discount'
import { cn } from '@/lib/utils'
import { Images } from 'lucide-react'

interface ImageGalleryDesktopProps {
  images: Image[] | undefined
  locationSlug: string
  unitSlug: string
  typeName?: string
  discount?: {
    minPrice: number
    maxPrice: number
  }
}

export function ImageGalleryDesktop({ images, locationSlug, unitSlug, typeName, discount }: ImageGalleryDesktopProps) {
  const sorted = [...(images ?? [])].sort((a, b) => b.priority - a.priority)
  const mainImage = sorted[0]
  const sideImages = sorted.slice(1, 5)

  // Mosaic shape follows how many photos there are: 1 + 4, 1 + 2, 1 + 1 or solo.
  const layout =
    sideImages.length >= 4 ? 'four' : sideImages.length >= 2 ? 'two' : sideImages.length === 1 ? 'one' : 'solo'
  const visibleSideImages =
    layout === 'four' ? sideImages.slice(0, 4) : layout === 'two' ? sideImages.slice(0, 2) : sideImages.slice(0, 1)

  if (!mainImage) {
    return <ImagePlaceholder className="h-105 w-full rounded-2xl" />
  }

  return (
    <div className="relative">
      <div
        className={cn(
          'grid h-105 gap-2 overflow-hidden rounded-2xl',
          layout === 'four' && 'grid-cols-4 grid-rows-2',
          layout === 'two' && 'grid-cols-3 grid-rows-2',
          layout === 'one' && 'grid-cols-3 grid-rows-1',
          layout === 'solo' && 'grid-cols-1 grid-rows-1',
        )}
      >
        <Link
          to="/ubytovani/$slug/$unitSlug/galerie"
          params={{ slug: locationSlug, unitSlug }}
          className={cn(layout !== 'solo' && 'col-span-2 row-span-2')}
        >
          <img
            src={mainImage.url.thumbnail}
            className="h-full w-full cursor-pointer object-cover transition-opacity hover:opacity-90"
            alt={mainImage.description || undefined}
          />
        </Link>
        {visibleSideImages.map((image, key) => (
          <Link key={key} to="/ubytovani/$slug/$unitSlug/galerie" params={{ slug: locationSlug, unitSlug }}>
            <img
              src={image.url.thumbnail}
              className="h-full w-full cursor-pointer object-cover transition-opacity hover:opacity-90"
              alt={image.description || undefined}
            />
          </Link>
        ))}
      </div>

      {/* Badges - bottom left over the main photo */}
      {(discount || typeName) && (
        <div className="absolute bottom-4 left-4 flex gap-2">
          {discount && discount.maxPrice > discount.minPrice && (
            <Discount minPrice={discount.minPrice} maxPrice={discount.maxPrice} />
          )}
          {typeName && <Badge variant="secondary">{typeName}</Badge>}
        </div>
      )}

      {sorted.length > 1 && (
        <Button
          asChild
          variant="outline"
          size="sm"
          className="absolute right-4 bottom-4 border-slate-200 bg-white/95 shadow-sm hover:bg-white"
        >
          <Link to="/ubytovani/$slug/$unitSlug/galerie" params={{ slug: locationSlug, unitSlug }}>
            <Images />
            Zobrazit všech {sorted.length} fotek
          </Link>
        </Button>
      )}
    </div>
  )
}
